import { MouseEvent, MouseEventHandler, ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    Button as PrimeButton,
    ButtonProps as PrimeButtonProps,
} from 'primereact/button';
import { IconUtils } from 'primereact/utils';
import clsx from 'clsx';

export interface ButtonProps extends PrimeButtonProps {
    iconSize?: 'sm';
    to?: string;
    tertiary?: boolean;
    outlined?: boolean;
    contrast?: boolean;
    iconButton?: boolean;
    'data-testid'?: string;
}

export function Button(props: ButtonProps): ReactNode {
    const navigate = useNavigate();
    const {
        to,
        tertiary,
        iconSize,
        size,
        className,
        onClick,
        contrast,
        iconButton,
        type,
        'data-testid': dataTestId,
        ...rest
    } = props;

    const testId =
        dataTestId ??
        (typeof rest.label === 'string'
            ? rest.label
                  .toLowerCase()
                  .replace(/[^a-z0-9]+/g, '-')
                  .replace(/^-+|-+$/g, '') || undefined
            : undefined);

    const handleOnClick = (e: MouseEvent<HTMLButtonElement>): void => {
        if (to) {
            navigate(to);
        }

        if (onClick) {
            onClick(e);
        }
    };

    const buttonClass = clsx(
        className,
        to || rest.link ? 'link' : null,
        iconSize ? `${'icon-size-' + iconSize}` : null,
        rest.iconPos ? 'icon-position-' + rest.iconPos : '',
        size ? 'button-size-' + size : '',
        iconButton ? 'icon-button' : null,
        { tertiary },
        { 'p-button-contrast': contrast },
    );

    const renderAsLink =
        Boolean(to) &&
        !rest.disabled &&
        !rest.loading &&
        !rest.tooltip &&
        !rest.badge &&
        !rest.children;

    if (to && renderAsLink) {
        const {
            label,
            icon,
            iconPos = 'left',
            severity,
            outlined,
            text,
            link,
            raised,
            rounded,
            plain,
            style,
            id,
            title,
            tabIndex,
            'aria-label': ariaLabel,
        } = rest;

        const linkClass = clsx(
            'p-button p-component',
            buttonClass,
            {
                'p-button-icon-only': Boolean(icon) && !label,
                'p-button-vertical':
                    (iconPos === 'top' || iconPos === 'bottom') &&
                    Boolean(label),
                'p-button-outlined': outlined,
                'p-button-raised': raised,
                'p-button-link': link,
                'p-button-text': text,
                'p-button-rounded': rounded,
                'p-button-plain': plain,
            },
            size ? `p-button-${size}` : null,
            severity ? `p-button-${severity}` : null,
        );

        return (
            <Link
                to={to}
                className={linkClass}
                style={style}
                id={id}
                title={title}
                tabIndex={tabIndex}
                aria-label={ariaLabel}
                onClick={
                    onClick as unknown as MouseEventHandler<HTMLAnchorElement>
                }
            >
                {IconUtils.getJSXIcon(
                    icon,
                    {
                        className: clsx(
                            'p-button-icon p-c',
                            label ? `p-button-icon-${iconPos}` : null,
                        ),
                    },
                    { props },
                )}
                {label ? (
                    <span className="p-button-label p-c">{label}</span>
                ) : (
                    <span
                        className="p-button-label p-c"
                        dangerouslySetInnerHTML={{ __html: '&nbsp;' }}
                    />
                )}
            </Link>
        );
    }

    return (
        <PrimeButton
            className={buttonClass}
            onClick={(e) => handleOnClick(e)}
            type={type ?? 'button'}
            data-testid={testId}
            {...rest}
        />
    );
}
